import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { CheckCircle2, Circle, ArrowRight, X, Rocket } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', slate: '#5C6B7A' };

export const FirstRunChecklist = ({ embedded = false }) => {
  const api = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const readOnly = ['regulator', 'investor'].includes(user?.role);   // a read-only login never binds a first policy

  const load = useCallback(() => {
    api.get('/first-run').then(r => setData(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    load();
    const onUpdate = () => load();
    window.addEventListener('firstrun-updated', onUpdate);
    return () => window.removeEventListener('firstrun-updated', onUpdate);
  }, [load]);

  const dismiss = async () => {
    setData(d => ({ ...d, dismissed: true }));
    api.post('/first-run/dismiss').catch(() => {});
  };

  if (readOnly || !data || data.dismissed) return null;
  if (!embedded && data.completed === data.total) return null;
  const pct = Math.round((data.completed / data.total) * 100);
  const allDone = data.completed === data.total;

  const body = (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Rocket className="w-4 h-4" style={{ color: C.teal }} />
          <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Guided first run</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-mono font-semibold" style={{ color: allDone ? C.green : C.blue }}>{data.completed}/{data.total}</span>
          {!embedded && (
            <button onClick={dismiss} title="Dismiss checklist" data-testid="first-run-dismiss"
              className="p-1 rounded hover:bg-black/5 transition-colors" style={{ color: C.slate }}>
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <p className="text-sm font-medium mt-1" style={{ color: C.navy }}>
        {allDone ? 'You\u2019re flying — first policy bound and delivered' : 'From first search to first bound policy'}
      </p>
      <div className="h-1 rounded-full mt-2 overflow-hidden" style={{ background: 'rgba(136,150,168,0.15)' }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: allDone ? C.green : C.teal }} />
      </div>
      <div className="mt-3 space-y-0.5">
        {data.steps.map((s, i) => (
          <button key={s.step} onClick={() => navigate(s.path)} data-testid={`first-run-step-${s.step}`}
            className="group w-full flex items-start gap-2.5 px-2 py-1.5 rounded-sm text-left transition-colors hover:bg-[rgba(26,76,124,0.05)]">
            {s.done
              ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.green }} />
              : <Circle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'rgba(136,150,168,0.5)' }} />}
            <span className="flex-1 min-w-0">
              <span className={`block text-xs font-medium ${s.done ? 'line-through' : ''}`} style={{ color: s.done ? C.slate : C.navy }}>
                {i + 1}. {s.title}
              </span>
              {!s.done && <span className="block text-[10px] mt-0.5" style={{ color: C.slate }}>{s.desc}</span>}
            </span>
            {!s.done && <ArrowRight className="w-3.5 h-3.5 mt-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: C.blue }} />}
          </button>
        ))}
      </div>
    </>
  );

  if (embedded) return <div className="text-left max-w-sm mx-auto" data-testid="first-run-checklist">{body}</div>;
  return (
    <Card className="p-4 rounded-sm" style={{ border: '1px solid rgba(0,180,216,0.3)' }} data-testid="first-run-checklist">
      {body}
    </Card>
  );
};

export default FirstRunChecklist;
